/** Dialog for pasting an agent's output from the Kaycoo platform.
 *
 *  Used when CMP's live call to a Kaycoo agent failed (CF 502, peer
 *  closed mid-stream) but the agent's run actually succeeded on the
 *  platform side and the structured output is visible on the Kaycoo
 *  dashboard. The reviewer pastes the JSON (or raw text) here; CMP
 *  parses it through the same salvage pipeline used for live responses
 *  and re-pauses the job at this stage so the normal review flow
 *  resumes. */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';

interface Props {
  jobId: string;
  stage: string;
  open: boolean;
  onClose: () => void;
  /** Fired after a successful ingest so the parent can re-fetch the row. */
  onIngested: () => void;
}

export function ManualIngestDialog({ jobId, stage, open, onClose, onIngested }: Props) {
  const [rawOutput, setRawOutput] = useState('');
  const [executionId, setExecutionId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setRawOutput('');
    setExecutionId('');
    setNote('');
    setError(null);
    setSubmitting(false);
  }

  async function handleSubmit() {
    if (!rawOutput.trim()) {
      setError('Paste the agent output before saving.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/v1/jobs/${jobId}/stages/${stage}/manual-ingest`, {
        raw_output: rawOutput,
        execution_id: executionId.trim() || undefined,
        note: note.trim() || undefined,
      });
      reset();
      onIngested();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Ingest failed. Please try again.';
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Paste agent output from Kaycoo</DialogTitle>
          <DialogDescription>
            CMP didn't receive this agent's response (upstream 502 or stream drop), but the
            execution succeeded on the Kaycoo platform. Paste the JSON shown on the Kaycoo
            dashboard and CMP will treat it as the agent's response for this stage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="raw-output" className="text-xs">
              Agent output (JSON or raw text)
            </Label>
            <textarea
              id="raw-output"
              value={rawOutput}
              onChange={(e) => { setRawOutput(e.target.value); }}
              rows={14}
              spellCheck={false}
              className="w-full rounded border border-input bg-background px-3 py-2 text-xs font-mono leading-relaxed focus-visible:ring-2 focus-visible:ring-ring resize-y"
              placeholder='{"clauses": [...], ...}'
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="execution-id" className="text-xs">
                Kaycoo execution_id (optional)
              </Label>
              <input
                id="execution-id"
                value={executionId}
                onChange={(e) => { setExecutionId(e.target.value); }}
                className="w-full rounded border border-input bg-background px-3 py-2 text-xs font-mono focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="exec_..."
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ingest-note" className="text-xs">
                Note (optional)
              </Label>
              <input
                id="ingest-note"
                value={note}
                onChange={(e) => { setNote(e.target.value); }}
                className="w-full rounded border border-input bg-background px-3 py-2 text-xs focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="cf-ray that failed, etc."
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => { void handleSubmit(); }} disabled={submitting || !rawOutput.trim()}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />}
            {submitting ? 'Saving…' : 'Ingest output'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
